import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import React from 'react'
import { toast } from 'react-toastify'

import {
  REFRESH_TICKET_TRACE_CONTEXT_MUTATION,
  TICKET_TRACE_LOGS_QUERY
} from '../../graphql/tickets'
import {
  formatWhen,
  TicketDiagnosticContext,
  TicketTraceLogPage
} from '../../helpers/ticketWorkflow'
import { maskTracePayload } from '../../helpers/tracePayload'
import { getTranslate } from '../../localization'
import SectionCard from './SectionCard'

interface DiagnosticContextPanelProps {
  ticketId: string
  canInspect: boolean
}

const PAGE_SIZE = 20

/**
 * Upper bound on retained rows, matching the `maxRetained` the ticketing lists
 * pass to `useCursorPage`. Trace logs have no natural ceiling — an agent holding
 * "load more" accumulates every page in one cache entry, and each row renders a
 * full request AND response body into the DOM, so an unbounded list degrades the
 * tab long before the operator gets a useful answer.
 *
 * This panel does not route through `useCursorPage` because it is a lazy query
 * whose page also carries a `context` object the hook's item-list shape does not
 * model; the retention rule is copied deliberately rather than the hook forced.
 */
const MAX_RETAINED_LOGS = 500

export default function DiagnosticContextPanel({
  ticketId,
  canInspect
}: DiagnosticContextPanelProps) {
  const [load, { data, loading, error, fetchMore, refetch }] = useLazyQuery<{
    ticketTraceLogs: TicketTraceLogPage
  }>(TICKET_TRACE_LOGS_QUERY, {
    variables: { ticketId, pageSize: PAGE_SIZE },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true
  })
  const [refresh, refreshState] = useMutation<{
    refreshTicketTraceContext: TicketDiagnosticContext
  }>(REFRESH_TICKET_TRACE_CONTEXT_MUTATION)

  const page = data?.ticketTraceLogs

  function refreshContext() {
    refresh({ variables: { ticketId } })
      .then(() => refetch?.({ ticketId, pageSize: PAGE_SIZE, pageToken: null }))
      .then(() => toast.success(getTranslate('Diagnostic context refreshed.')))
      .catch(() =>
        toast.error(getTranslate('Diagnostic context could not be refreshed.'))
      )
  }

  function loadMore() {
    if (!page?.nextPageToken) return
    fetchMore({
      variables: {
        ticketId,
        pageSize: PAGE_SIZE,
        pageToken: page.nextPageToken
      },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous
        const merged = [
          ...previous.ticketTraceLogs.logs,
          ...fetchMoreResult.ticketTraceLogs.logs
        ]
        return {
          ticketTraceLogs: {
            ...fetchMoreResult.ticketTraceLogs,
            // Keep the newest window; older rows drop off the front the same
            // way `useCursorPage` bounds the ticketing lists.
            logs: merged.slice(-MAX_RETAINED_LOGS)
          }
        }
      }
    }).catch(() =>
      toast.error(getTranslate('More diagnostic logs could not be loaded.'))
    )
  }

  return (
    <SectionCard
      heading={getTranslate('Diagnostic context attached')}
      level="h3"
      quiet>
      <Typography variant="body2" color="textSecondary">
        {/* The old copy said raw logs "require separate access", which read as
            though they were not shown here — they are, in the Payload column.
            Say what the panel actually does instead.

            Only when the reader can actually see them. Promising content
            "shown below" and then rendering nothing is worse than saying
            nothing: it reads as a broken panel rather than a closed door. */}
        {canInspect
          ? getTranslate(
              'The ticket keeps a bounded trace reference. Captured request and response bodies are shown below; obvious credentials are masked, but treat them as sensitive.'
            )
          : getTranslate(
              'The ticket keeps a bounded trace reference. Viewing the captured request and response bodies needs the monitoring permission.'
            )}
      </Typography>

      {!canInspect ? null : !page && !loading && !error ? (
        <Button
          size="small"
          variant="outlined"
          style={{ marginTop: 8 }}
          onClick={() => load()}>
          {getTranslate('View diagnostic logs')}
        </Button>
      ) : null}

      {loading && !page && (
        <div style={{ marginTop: 12 }}>
          <CircularProgress size={20} />
        </div>
      )}

      {error && (
        <Alert severity="warning" style={{ marginTop: 12 }}>
          {getTranslate(
            'Diagnostic logs are unavailable for this account or could not be loaded.'
          )}
        </Alert>
      )}

      {page && (
        <div style={{ marginTop: 12 }}>
          <div
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
            <Chip
              size="small"
              label={getTranslate(`Diagnostic context ${page.context.status}`)}
            />
            <Typography variant="caption" color="textSecondary">
              {getTranslate('Snapshot')} {page.context.snapshotVersion}
              {page.context.capturedAt
                ? ` · ${formatWhen(page.context.capturedAt)}`
                : ''}
              {page.context.services.length
                ? ` · ${page.context.services.join(', ')}`
                : ''}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              disabled={refreshState.loading || loading}
              onClick={refreshContext}>
              {getTranslate('Refresh diagnostic context')}
            </Button>
          </div>

          {page.logs.length ? (
            <TableContainer style={{ marginTop: 12 }}>
              <Table size="small" aria-label={getTranslate('Diagnostic logs')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{getTranslate('Time')}</TableCell>
                    <TableCell>{getTranslate('Service')}</TableCell>
                    <TableCell>{getTranslate('Endpoint')}</TableCell>
                    <TableCell>{getTranslate('Status')}</TableCell>
                    <TableCell>{getTranslate('Payload')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {page.logs.map((log, index) => (
                    <TableRow key={`${log.date}-${log.service}-${index}`}>
                      <TableCell>
                        <bdi dir="ltr">{formatWhen(log.date)}</bdi>
                      </TableCell>
                      <TableCell>
                        <bdi dir="ltr">{log.service}</bdi>
                      </TableCell>
                      <TableCell>
                        <bdi dir="ltr">
                          {log.endpointName || log.endpointURL || '—'}
                        </bdi>
                      </TableCell>
                      <TableCell>{log.status}</TableCell>
                      <TableCell>
                        <details>
                          <summary>{getTranslate('View payload')}</summary>
                          <Typography
                            component="pre"
                            variant="caption"
                            style={{
                              whiteSpace: 'pre-wrap',
                              overflowWrap: 'anywhere'
                            }}>
                            {maskTracePayload(log.request) || '—'}
                            {'\n\n'}
                            {maskTracePayload(log.response) || '—'}
                          </Typography>
                        </details>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info" style={{ marginTop: 12 }}>
              {getTranslate('No logs matched this diagnostic trace window.')}
            </Alert>
          )}

          {page.nextPageToken && (
            <Button
              size="small"
              variant="outlined"
              disabled={loading}
              style={{ marginTop: 12 }}
              onClick={loadMore}>
              {getTranslate('Load more')}
            </Button>
          )}
        </div>
      )}
    </SectionCard>
  )
}
